import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Settings, Save, Loader2, Zap } from 'lucide-react';

const RETENTION_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
  { value: 730, label: '2 years' },
  { value: 1825, label: '5 years' },
  { value: 2555, label: '7 years (default)' },
  { value: 3650, label: '10 years' },
];

export function BackupSettingsForm({
  settings,
  setSettings,
  isSuperAdmin,
  savingSettings,
  onSave,
  runningAutoBackup,
  onRunAutoBackupNow,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Backup Settings
        </CardTitle>
        <CardDescription>Configure automatic backup schedule and retention policy</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Automatic Daily Backups</Label>
            <p className="text-xs text-muted-foreground">Automatically create a backup every day</p>
          </div>
          <Switch
            checked={settings.auto_backup_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, auto_backup_enabled: v })}
            disabled={!isSuperAdmin}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Backup Time</Label>
            <Input
              type="time"
              value={settings.backup_time}
              onChange={(e) => setSettings({ ...settings, backup_time: e.target.value })}
              disabled={!isSuperAdmin}
            />
            <p className="text-xs text-muted-foreground">Time when daily backup runs</p>
          </div>

          <div className="space-y-2">
            <Label>Data Retention Period</Label>
            <Select
              value={settings.retention_days?.toString()}
              onValueChange={(v) => setSettings({ ...settings, retention_days: parseInt(v) })}
              disabled={!isSuperAdmin}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RETENTION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">How long to keep backups</p>
          </div>

          <div className="space-y-2">
            <Label>Maximum Backups</Label>
            <Input
              type="number"
              min="10"
              max="500"
              value={settings.max_backups}
              onChange={(e) => setSettings({ ...settings, max_backups: parseInt(e.target.value) })}
              disabled={!isSuperAdmin}
            />
            <p className="text-xs text-muted-foreground">Max number of backups to keep</p>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="pt-2 flex flex-wrap gap-2">
            <Button onClick={onSave} disabled={savingSettings} data-testid="btn-save-backup-settings">
              {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>

            {/* Manual "Run Auto-Backup Now" — useful when the daily
                schedule was missed (container downtime around the
                trigger window) or when the operator wants an off-
                schedule snapshot. Uses the exact same code path as
                the scheduled run. */}
            {onRunAutoBackupNow && (
              <Button
                type="button"
                variant="outline"
                onClick={onRunAutoBackupNow}
                disabled={runningAutoBackup || !settings.auto_backup_enabled}
                data-testid="btn-run-auto-backup-now"
                title={
                  !settings.auto_backup_enabled
                    ? 'Enable auto-backup above to use this'
                    : 'Run the scheduled auto-backup right now'
                }
              >
                {runningAutoBackup
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Zap className="w-4 h-4 mr-2" />}
                Run Auto-Backup Now
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
